import { useReadOnly } from '@/lib/readOnly';
import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cx } from '@/lib/cx';

export interface DrawerProps {
  className?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Left is reserved for navigation; all other uses open as centered popups. */
  side?: 'right' | 'left';
  dismissible?: boolean;
  /** Wrap body+footer in a <form> so Enter submits and the footer submit button works. */
  onSubmit?: () => void;
}

export function Drawer({ className, open, onOpenChange, title, description, children, footer, size = 'md', side = 'right', dismissible = true, onSubmit }: DrawerProps) {
  const readOnly = useReadOnly();
  const inner = (
    <>
      <div className="drawer-body">{children}</div>
      {footer && <footer className="drawer-foot">{footer}</footer>}
    </>
  );
  return (
    <Dialog.Root open={open} onOpenChange={(o) => (dismissible || o ? onOpenChange(o) : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay" />
        <Dialog.Content
          className={cx('drawer', `drawer-${size}`, side === 'left' ? 'drawer-left' : 'app-popup', className)}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
        >
          <span className="sheet-grabber" aria-hidden />
          <header className="drawer-head">
            <div className="modal-head-text">
              <Dialog.Title className="modal-title">{title}</Dialog.Title>
              {description ? <Dialog.Description className="modal-desc">{description}</Dialog.Description> : <Dialog.Description className="sr-only">Panel</Dialog.Description>}
            </div>
            <Dialog.Close asChild>
              <button type="button" className="modal-close" aria-label="Close" disabled={!dismissible}>
                <X size={16} />
              </button>
            </Dialog.Close>
          </header>
          {onSubmit ? (
            <form
              className="drawer-form"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (!readOnly) onSubmit();
              }}
            >
              {inner}
            </form>
          ) : (
            inner
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
